import {
  Play,
  Save,
  FilePlus,
  FileCode2,
  Code,
  Bookmark,
} from 'lucide-react';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useConnectionStore } from '@/stores/connection-store';
import { useToastStore } from '@/stores/toast-store';
import type { Command } from '../types';

/**
 * Commands that act on whatever query tab is focused. The `when()` guards
 * hide them everywhere else so the palette stays tight.
 */
function isQueryTab(tab: unknown): tab is { type: 'query'; id: string; sql: string; title: string; connectionId: string } {
  return !!tab && typeof tab === 'object' && (tab as { type?: string }).type === 'query';
}

export const queryCommands: Command[] = [
  {
    id: 'query.run',
    label: 'Run Query',
    icon: Play,
    category: 'Query',
    shortcut: 'Ctrl+Enter',
    keywords: ['execute'],
    when: (ctx) => isQueryTab(ctx.activeTab),
    // Delegate to the editor — Monaco's Ctrl+Enter binding already runs
    // the query. We fire it via a custom event rather than wiring the
    // editor's ref in here.
    run: () => {
      window.dispatchEvent(new CustomEvent('dbgo:query:run'));
    },
  },
  {
    id: 'query.format',
    label: 'Format SQL',
    icon: Code,
    category: 'Query',
    keywords: ['beautify', 'pretty'],
    when: (ctx) => isQueryTab(ctx.activeTab),
    run: () => {
      window.dispatchEvent(new CustomEvent('dbgo:query:format'));
    },
  },
  {
    id: 'query.save',
    label: 'Save Query',
    icon: Save,
    category: 'Query',
    shortcut: 'Ctrl+S',
    when: (ctx) => isQueryTab(ctx.activeTab),
    run: () => {
      window.dispatchEvent(new CustomEvent('dbgo:query:save'));
    },
  },
  {
    id: 'query.save-as',
    label: 'Save Query as New...',
    icon: FilePlus,
    category: 'Query',
    shortcut: 'Ctrl+Shift+S',
    when: (ctx) => isQueryTab(ctx.activeTab),
    run: () => {
      window.dispatchEvent(new CustomEvent('dbgo:query:save-as'));
    },
  },
  {
    id: 'query.snippets',
    label: 'Insert SQL Snippet...',
    icon: FileCode2,
    category: 'Query',
    keywords: ['template'],
    when: (ctx) => isQueryTab(ctx.activeTab),
    run: () => {
      window.dispatchEvent(new CustomEvent('dbgo:query:snippets'));
    },
  },
  {
    id: 'query.open-saved',
    label: 'Open Saved Query...',
    icon: Bookmark,
    category: 'Query',
    keywords: ['load'],
    run: () => {
      window.dispatchEvent(new CustomEvent('dbgo:command-bar:open', {
        detail: { prefill: '@query ' },
      }));
    },
  },
  {
    id: 'query.new-for-active',
    label: 'New Query (for active connection)',
    icon: FileCode2,
    category: 'Query',
    keywords: ['editor', 'sql'],
    when: (ctx) => ctx.activeConnectionId !== null,
    run: (ctx) => {
      if (!ctx.activeConnectionId) return;
      const { activeConnections } = useConnectionStore.getState();
      if (activeConnections[ctx.activeConnectionId]?.status !== 'connected') {
        useToastStore.getState().addToast('warning', 'Connect to the database first.');
        return;
      }
      useWorkspaceStore.getState().openTab({
        type: 'query',
        id: `query-${Date.now()}`,
        connectionId: ctx.activeConnectionId,
        title: 'New Query',
        sql: '',
      });
    },
  },
];
